import { useAtom } from "jotai";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ButtonLink, SubmitButton } from "@/components/Interface/Button/Button";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { AuthenticatorSmsConfirmForm } from "@/components/Welcome/AuthenticatorSetup/AuthenticatorSmsConfirmForm";
import { TwoFactorConfirmationCodeForm } from "@/components/Welcome/AuthenticatorSetup/TwoFactorConfirmationInput";
import { useRequestSmsToken } from "@/hooks/useRequestSmsToken";
import {
  useSetUpTwoFactorAuthentication,
  useValidateTwoFactorAuthentication,
} from "@/services/auth";
import { ValidateTwoFactorAuthenticationMethod } from "@/services/model";
import { phoneNumberAtom, twoFactorBackupCodesAtom } from "@/utils/atoms";

// TODO: This component is very similar to TwoFactorSmsConfirm.tsx
export const AuthenticatorSmsConfirm = ({
  navigationTarget,
}: {
  navigationTarget: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [phoneNumber] = useAtom(phoneNumberAtom);
  const [, setBackupCodes] = useAtom(twoFactorBackupCodesAtom);
  const formMethods = useForm<TwoFactorConfirmationCodeForm>({
    defaultValues: {
      code: "",
    },
  });
  const { mutateAsync: validateTwoFactorAuth } =
    useValidateTwoFactorAuthentication({
      mutation: {
        onError: () => {
          formMethods.setError("code", {
            type: "manual",
            message: t("portal:authenticator.app.setup.step2.invalidCode"),
          });
        },
      },
    });

  const resendParams = {
    method: ValidateTwoFactorAuthenticationMethod.SMS,
    phoneNumber,
  };

  const resendAction = useRequestSmsToken(
    useSetUpTwoFactorAuthentication,
    resendParams,
  );

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit((data) => {
          validateTwoFactorAuth({
            params: {
              method: ValidateTwoFactorAuthenticationMethod.SMS,
              code: parseInt(data.code),
            },
          }).then((data) => {
            if (data.backupCodes) {
              setBackupCodes(data.backupCodes);
              navigate(navigationTarget);
            }
          });
        })}
      >
        <ProfileCard
          maxWidth="none"
          title={t("portal:user-account.security.2fa.initialSetup.title")}
          actions={
            <>
              <ButtonLink variant="secondary" size="sm" to={-1 as any}>
                {t("portal:user-account.security.buttons.back")}
              </ButtonLink>
              <SubmitButton
                fullWidth={false}
                size="sm"
                label={t("portal:user-account.security.buttons.verify")}
              />
            </>
          }
        >
          <AuthenticatorSmsConfirmForm resendAction={resendAction} />
        </ProfileCard>
      </form>
    </FormProvider>
  );
};
